import axios from "axios";
import { getActiveCompanyId } from "./companyContext";
import {
  clearStoredIdToken,
  getStoredIdToken,
  getStoredTokenExpiryMs,
  getTokenExpiryFromJwt,
} from "./tokenUtils";
import { auth } from "./firebase";
import { getFreshIdToken } from "./firebaseAuth";
import { requireI9ApiBase } from "./utils";

const EXPIRY_SAFETY_WINDOW_MS = 2 * 60 * 1000;

export const getValidIdToken = async () => {
  if (typeof window === "undefined") {
    return null;
  }
  let idToken = getStoredIdToken();
  let expiryMs = getStoredTokenExpiryMs();

  if (idToken && expiryMs === null) {
    const computedExpiry = getTokenExpiryFromJwt(idToken);
    if (computedExpiry) {
      try {
        window.localStorage?.setItem("idTokenExpiryMs", String(computedExpiry));
      } catch {
        // localStorage might be unavailable
      }
      expiryMs = computedExpiry;
    }
  }

  const now = Date.now();
  const shouldRefresh =
    !idToken || (expiryMs !== null && expiryMs - now <= EXPIRY_SAFETY_WINDOW_MS);

  if (shouldRefresh && auth.currentUser) {
    const force = expiryMs !== null && expiryMs <= now;
    const firebaseToken = await getFreshIdToken(force);
    if (firebaseToken) {
      idToken = firebaseToken;
    }
  }

  return idToken;
};

export const api = axios.create({
  baseURL: requireI9ApiBase(),
});

api.interceptors.request.use(async (config) => {
  config.headers = config.headers ?? {};

  const token = await getValidIdToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const activeCompanyId = getActiveCompanyId();
  const hasCustomCompanyHeader =
    config.headers["x-company-id"] !== undefined &&
    config.headers["x-company-id"] !== null &&
    config.headers["x-company-id"] !== "";

  if (!hasCustomCompanyHeader) {
    if (activeCompanyId) {
      config.headers["x-company-id"] = activeCompanyId;
    } else {
      delete config.headers["x-company-id"];
    }
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error?.response?.status;

    const retryCount = originalRequest._retryCount || 0;
    if (status === 401 && retryCount < 1) {
      originalRequest._retryCount = retryCount + 1;
      const refreshed = auth.currentUser ? await getFreshIdToken(true) : null;
      if (refreshed) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshed}`;
        return api(originalRequest);
      }

      clearStoredIdToken();
      if (typeof window !== "undefined" && window.location.pathname !== "/") {
        window.location.pathname = "/";
      }
    }

    return Promise.reject(error);
  }
);
